import React, {Fragment, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import window from "../../../lib/WindowExtended";
import {getShopId} from "../../../lib/getShopId";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, scrollToTop} from "../../Checkout";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {IAddressData, IAddressResponseData, ICountriesLookup} from "@connox/typescript-library";
import AddressForm, {IAddressFormData} from "./AddressForm";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import Spinner from "../../../utils/loader/Spinner";
import {ICartRequestHandlerPutCartOptions} from "@connox/typescript-library/types/cart/requestHandlers/CartRequestHandler";
import * as Sentry from "@sentry/browser";
import styles from "./AddressStep.module.scss";
import genericStyles from "../../../lib/GenericStyles.module.scss";
import isSameObject from "../../../../helpers/isSameObject";
import {useForm} from "react-hook-form";
import {useCustomerSelector} from "../../../../store";

type IAddressesState = {
  isInvoiceSameAsDelivery: boolean;
  deliveryAddress: IAddressFormData;
  invoiceAddress: IAddressFormData;
  isCorporateCustomer?: boolean;
};

// eslint-disable-next-line complexity
const AddressStep: React.FC<{isVisible: boolean}> = ({isVisible}) => {
  const {t} = useTranslation(["errorcodes", "translations"]);
  const {countries} = useCartContext();
  const {checkout} = useCheckoutContext();
  const {customer} = useCustomerSelector();
  const {countryCode, isCorporateCustomer} = checkout.cartData;
  const {viewState, cart} = useCartContext();
  // const [countries, setCountries] = useState<ICountriesLookup[]>([]);
  const [deliveryCountries, setDeliveryCountries] = useState<ICountriesLookup[]>(countries);
  // will set to true when we are waiting for whatever request to come back
  const [isLoading, setIsLoading] = useState(false);
  // save possible errors or bad messages in checkout/cart/positions after every api call
  const [errors, setErrors] = useState<(string | JSX.Element)[]>([]);
  // save possible phone unexpted phone number error
  const [phoneError, setPhoneError] = useState<string>("");
  // save the country code that is not allowed for shipping( this error comes in cart in positions level)
  const [shippingErrorCountryCode, setShippingErrorCountryCode] = useState<string>("");
  const [defaultAddresses, setDefaultAddresses] = useState<{delivery: IAddressFormData; invoice: IAddressFormData}>({
    delivery: {...emptyAddress, vatNumber: ""},
    invoice: {...emptyAddress, vatNumber: ""},
  });
  const [readyToSubmit, setReadyToSubmit] = useState<{delivery: boolean; invoice: boolean}>({
    delivery: false,
    invoice: false,
  });

  // main state for all addresses
  const invAdd = (checkout.invoiceAddress ?? {...emptyAddress, countryCode}) as IAddressFormData;
  const delAdd = (
    checkout.deliveryAddress ? {...checkout.deliveryAddress, countryCode} : {...invAdd, countryCode}
  ) as IAddressFormData;

  const initialState: IAddressesState = {
    isInvoiceSameAsDelivery: checkout.deliveryAddress ? isSameAddress(invAdd, delAdd) : true,
    invoiceAddress: invAdd,
    deliveryAddress: delAdd,
    isCorporateCustomer,
  };

  const [addressesState, setAddressesState] = useState<IAddressesState>(initialState);

  const defaultInvoiceCountryCode =
    !isSameAddress(checkout.deliveryAddress, checkout.invoiceAddress) &&
    checkout.deliveryAddress &&
    checkout.invoiceAddress
      ? checkout.invoiceAddress.countryCode
      : countryCode;

  const email = checkout.invoiceAddress?.email || defaultAddresses.delivery.email || customer.email || "";

  const methodsInvoice = useForm<IAddressFormData>({
    mode: "onSubmit",
    defaultValues: {
      ...(checkout.invoiceAddress || defaultAddresses.invoice),
      vatNumber: checkout.cartData.vatNumber,
      countryCode: defaultInvoiceCountryCode,
      email,
    },
  });

  const methodsDelivery = useForm<IAddressFormData>({
    mode: "onSubmit",
    defaultValues: {
      ...(checkout.deliveryAddress || defaultAddresses.delivery),
      vatNumber: "",
      countryCode,
      email,
    },
  });

  // effect to get all countries when the components get mounted, here will initialize some states variables
  useEffect(() => {
    // in case the invoice country cant be delivered expand delivery address form
    const invoiceCountryCode = addressesState.invoiceAddress.countryCode;
    const isInvoiceCountryAllowedForDelivery = countries.some((country) => country.countryCode === invoiceCountryCode);
    setShippingErrorCountryCode(isInvoiceCountryAllowedForDelivery ? "" : invoiceCountryCode);
    if (!isInvoiceCountryAllowedForDelivery) {
      setAddressesState({...addressesState, isInvoiceSameAsDelivery: false});
    }

    if (viewState.shippingErrors.length) {
      setErrors(viewState.shippingErrors.map((err) => t(err)));
    }
  }, []);

  // set last addresses just in case invoice address is null
  useEffect(() => {
    if (customer.isRecognised && !checkout.invoiceAddress) {
      void customer.getLastAddresses(getShopId()).then((addresses) => {
        const lastInvoiceAddress = addresses.find((address) => address.isLastInvoiceAddress) ?? null;
        const lastDeliveryAddress = addresses.find((address) => address.isLastDeliveryAddress) ?? null;
        const isSameAddresses =
          lastInvoiceAddress?.addressId === lastDeliveryAddress?.addressId ||
          isSameAddress(lastInvoiceAddress, lastDeliveryAddress);
        const equippedInvoiceAddress = {
          ...(lastInvoiceAddress ?? emptyAddress),
          vatNumber: checkout.cartData.vatNumber,
        };
        const equippedDeliveryAddress = {
          ...(lastDeliveryAddress ?? emptyAddress),
          vatNumber: "",
        };

        setAddressesState({
          ...addressesState,
          invoiceAddress: isSameAddresses ? {...equippedInvoiceAddress, countryCode} : equippedInvoiceAddress,
          deliveryAddress: {...equippedDeliveryAddress, countryCode},
          isInvoiceSameAsDelivery: isSameAddresses,
        });

        setDefaultAddresses({
          delivery: equippedDeliveryAddress,
          invoice: equippedInvoiceAddress,
        });
      });
    }
  }, [customer.isLoggedIn]);

  // recalculate the shipping cost after every change to the delivery country under some conditions
  useEffect(() => {
    if (
      !addressesState.isInvoiceSameAsDelivery &&
      addressesState.deliveryAddress?.countryCode &&
      addressesState.deliveryAddress?.countryCode !== countryCode &&
      addressesState.deliveryAddress?.countryCode !== shippingErrorCountryCode &&
      isVisible
    ) {
      window.isIntoChangingCountry = true;
      void submitAddress();
    }
  }, [addressesState.deliveryAddress?.countryCode]);

  // make a call to recalculate the shipping cost after every change to the invoice  country under some conditions
  // make a call when the isInvoiceSameAsDelivery state changes.
  useEffect(() => {
    if (countries.length && addressesState.invoiceAddress) {
      const invoiceCountryCode = addressesState.invoiceAddress?.countryCode;
      const isInvoiceCountryAllowedForDelivery = countries.find(
        (country) => country.countryCode === invoiceCountryCode && invoiceCountryCode !== shippingErrorCountryCode,
      );
      setShippingErrorCountryCode(isInvoiceCountryAllowedForDelivery ? "" : invoiceCountryCode);
      if (!isInvoiceCountryAllowedForDelivery) {
        setAddressesState({...addressesState, isInvoiceSameAsDelivery: false});
      } else {
        if (
          addressesState.isInvoiceSameAsDelivery &&
          isVisible &&
          addressesState.invoiceAddress?.countryCode !== countryCode
        ) {
          window.isIntoChangingCountry = true;
          void submitAddress();
        }
      }
    }
  }, [addressesState.invoiceAddress?.countryCode, addressesState.isInvoiceSameAsDelivery]);

  // more  filtering for  delivery countries.
  useEffect(() => {
    if (shippingErrorCountryCode) {
      setDeliveryCountries(countries.filter((country) => country.countryCode !== shippingErrorCountryCode));
    } else {
      setDeliveryCountries(countries);
    }
  }, [shippingErrorCountryCode]);

  // this effect will check possible cart shipping error and save this in the state
  useEffect(() => {
    const {positions} = checkout.cartData;
    positions.forEach((pos) => {
      if (pos.messages) {
        pos.messages.forEach((msg) => {
          if (msg.code === "NOT_ALLOWED_FOR_COUNTRY" || msg.code === "SHIPPING_NOT_FOUND") {
            setShippingErrorCountryCode(checkout.cartData?.countryCode);
            setAddressesState({...addressesState, isInvoiceSameAsDelivery: false});
          }
        });
      } else {
        setShippingErrorCountryCode("");
      }
    });
  }, [checkout.cartData]);

  useEffect(() => {
    if (errors.length) {
      setErrors([]);
    }
    if (readyToSubmit.delivery && readyToSubmit.invoice) {
      setReadyToSubmit({delivery: false, invoice: false});
      void submitAddress();
    }
  }, [readyToSubmit.delivery, readyToSubmit.invoice]);

  const invoiceAddr = () => convertToAddressData(addressesState.invoiceAddress);

  const deliveryAddr = () =>
    addressesState.isInvoiceSameAsDelivery
      ? invoiceAddr()
      : convertToAddressData({...addressesState.deliveryAddress, email: addressesState.invoiceAddress.email});

  const isCorporateCustomerOptionsNeeded = (): boolean =>
    checkout.cartData.isCorporateCustomer && addressesState.invoiceAddress?.vatNumber !== checkout.cartData.vatNumber;

  const handleInvoiceSubmit = methodsInvoice.handleSubmit(() =>
    setReadyToSubmit((prevState) => ({...prevState, invoice: true})),
  );

  const handleDeliverySubmit = methodsDelivery.handleSubmit(() =>
    setReadyToSubmit((prevState) => ({...prevState, delivery: true})),
  );

  // this function will submit the addresses state
  const submitAddress = async () => {
    if (!addressesState.invoiceAddress) {
      return;
    }

    setIsLoading(true);
    const invoiceAddress = invoiceAddr();

    const deliveryAddress = deliveryAddr();

    if (isToNorthIreland(deliveryAddress.countryCode, deliveryAddress.zip)) {
      setErrors([t("checkout.noShippingToSelectedAddress")]);
      setIsLoading(false);

      return;
    }

    try {
      let cartOptions: ICartRequestHandlerPutCartOptions = {};
      const {isNet} = checkout.cartData;

      if (isCorporateCustomerOptionsNeeded()) {
        cartOptions = {vatNumber: addressesState.invoiceAddress?.vatNumber ?? ""};
      }
      /*
       * If customer is not just into changing country (extra request necessary
       * for shipping cost calculation in the cart detaisl) but into continuing
       * and only if country really changes ('useEffect' side effects)
       */
      if (deliveryAddress.countryCode && deliveryAddress.countryCode !== checkout.cartData.countryCode) {
        cartOptions = {...cartOptions, countryCode: deliveryAddress.countryCode};
      }
      if (Object.keys(cartOptions).length) {
        await cart.put(cartOptions);
      }
      await checkout.put({
        address: {
          delivery: deliveryAddress,
          invoice: invoiceAddress,
        },
        paymentId: checkout.paymentId ?? undefined,
      });

      setIsLoading(false);
      if (!window.isIntoChangingCountry) {
        scrollToTop();
      }

      if (isNet !== checkout.cartData.isNet) {
        await cart.put({});
      }
      setPhoneError("");
    } catch (err) {
      /* eslint-disable */
      const errMessage: string =
        err?.originalError?.response?.data.result.code || err?.originalError?.message || err.message;
      /* eslint-enable */
      const sentryEventId = Sentry.captureException(err);
      setIsLoading(false);
      if (errMessage.includes("PHONE")) {
        setPhoneError("");
        setPhoneError(errMessage);
      } else {
        setErrors([t(errMessage, t(DEFAULT_ERROR_MESSAGE, {sentryEventId}))]);
      }
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${genericStyles.checkoutContainer} ${genericStyles.mobileOnlyFriendly}`}>
      <CheckoutError>{errors}</CheckoutError>
      <h1>{t("form.yourInvoiceAddress")}</h1>
      {!!countries.length && (
        <AddressForm
          onChange={(address) => {
            setAddressesState((prevState) => ({...prevState, invoiceAddress: address}));
          }}
          methods={methodsInvoice}
          phoneError={phoneError}
          countries={countries}
          lastAddress={defaultAddresses.invoice}
        />
      )}

      <div className={styles.checkboxIsSameAddress}>
        <input
          type={"checkbox"}
          id={"isSameAddressCheckBox"}
          checked={addressesState.isInvoiceSameAsDelivery && !shippingErrorCountryCode}
          disabled={!!shippingErrorCountryCode}
          onChange={() => {
            setAddressesState((prevState) => ({
              ...prevState,
              isInvoiceSameAsDelivery: !prevState?.isInvoiceSameAsDelivery,
            }));
          }}
        />
        <label htmlFor={"isSameAddressCheckBox"}>{t("checkout.sameAddress")}</label>
      </div>

      {(!addressesState.isInvoiceSameAsDelivery || !!shippingErrorCountryCode) && (
        <Fragment>
          {!!shippingErrorCountryCode && (
            <p>
              {t("checkout.shippingNotAvailableInSelectedCountry", {
                country: countries.find((country) => country.countryCode === shippingErrorCountryCode)?.name || "",
                addressType:
                  shippingErrorCountryCode === addressesState.invoiceAddress?.countryCode
                    ? t("general.invoiceAddress")
                    : t("general.deliveryAddress"),
              })}
              {["DK", "GB", "CH", "DE", "FR", "NL"].includes(shippingErrorCountryCode) && (
                <React.Fragment>
                  <span>{" " + t("general.orVisitLocalShop")}</span>
                  <a href={"https://" + getShopUrl[shippingErrorCountryCode]}>
                    {" " + getShopUrl[shippingErrorCountryCode]}
                  </a>
                </React.Fragment>
              )}
              {"."}
            </p>
          )}
          <h1>{t("form.yourDeliveryAddress")}</h1>
          {!!deliveryCountries.length && (
            <AddressForm
              isDelivery={true}
              methods={methodsDelivery}
              onChange={(address) => {
                setAddressesState((prevState) => ({...prevState, deliveryAddress: address}));
              }}
              phoneError={phoneError}
              countries={deliveryCountries}
              lastAddress={defaultAddresses.delivery}
            />
          )}
        </Fragment>
      )}

      {!customer.isLoggedIn && (
        <div className={styles.checkboxIsSameAddress}>
          <input
            type={"checkbox"}
            id={"isCorporateCustomer"}
            checked={addressesState.isCorporateCustomer}
            onChange={() => {
              setAddressesState((prevState) => ({
                ...prevState,
                isCorporateCustomer: !prevState?.isCorporateCustomer,
              }));
              void checkout.put({
                cart: {
                  isCorporateCustomer: !addressesState.isCorporateCustomer,
                },
              });
            }}
          />
          <label htmlFor={"isCorporateCustomer"}>
            {t("checkout.businessCustomerPurchase")}
            {t("general.optional")}
          </label>
        </div>
      )}

      <p style={{fontSize: ".875rem"}}>**&nbsp;{t("form.mandatory__other")}</p>
      <CartDetails />
      <Button
        action={async (evt) => {
          evt.preventDefault();
          window.isIntoChangingCountry = false;
          await handleInvoiceSubmit();
          await handleDeliverySubmit();
        }}
        styles={[ButtonStyles.wide]}
        type={isLoading ? ButtonType.disabled : ButtonType.default}
      >
        {isLoading ? <Spinner /> : <span>{t("general.continue")}</span>}
      </Button>
    </div>
  );
};

export const isToNorthIreland = (countryCode: string, zip: string): boolean => {
  return countryCode === "GB" && zip.toUpperCase().startsWith("BT");
};

export const isSameAddress = (invoice: IAddressResponseData | null, delivery: IAddressResponseData | null): boolean => {
  const keysToBeIgnored = ["addressId", "messages", "isLastInvoiceAddress", "isLastDeliveryAddress", "rawData"];

  return isSameObject<IAddressResponseData>(invoice, delivery, keysToBeIgnored);
};

export const getShopUrl: {[countryCode: string]: string} = {
  CH: getShopId() === 100008 ? "fr.connox.ch" : "www.connox.ch",
  DK: "www.connox.dk",
  GB: "www.connox.co.uk",
  DE: "www.connox.de",
  FR: "www.connox.fr",
};

export const convertToAddressData = (
  data: Partial<IAddressFormData>,
): IAddressData & {addressSupplement: string | null} => {
  let addressSupplement: string | null = null;
  if (data.street && ["Packstation", "Postfiliale"].includes(data.street)) {
    addressSupplement = data.addressSupplement || null;
  }

  return {
    email: data.email || "",
    firstName: data.firstName || "",
    gender: data.gender as "M" | "F",
    lastName: data.lastName || "",
    addressSupplement,
    city: data.city || "",
    company: data.company || "",
    countryCode: data.countryCode || "",
    dateOfBirth: data.dateOfBirth || null,
    department: data.department || "",
    number: data.number || "",
    phone: data.phone || "",
    state: data.state || "",
    street: data.street || "",
    title: data.title || "",
    zip: data.zip || "",
  };
};

export const emptyAddress: IAddressResponseData = {
  addressId: "",
  addressSupplement: null,
  city: "",
  company: "",
  countryCode: "",
  dateOfBirth: "",
  department: "",
  email: "",
  firstName: "",
  gender: "" as "M" | "F",
  isLastDeliveryAddress: false,
  isLastInvoiceAddress: false,
  lastName: "",
  number: "",
  phone: "",
  state: "",
  street: "",
  title: "",
  zip: "",
};

export default AddressStep;
